import { IBase } from "./base";
import { Organization } from "./organization";
import { Locker } from "./locker";
import { CabinetConfigDevice } from "./device";
import { Puc, PucPayload } from "./puc";
import { LightingPayload } from "./lighting";

interface ICabinetProperties {
  // The following interfaces are not specified in order to aid testing; see
  // /dev for the implied complete interface
  // eslint-disable-next-line @typescript-eslint/ban-types
  app: any;
  // eslint-disable-next-line @typescript-eslint/ban-types
  modbus_connection: object;
  // eslint-disable-next-line @typescript-eslint/ban-types
  serial_connection: object;
  // eslint-disable-next-line @typescript-eslint/ban-types
  hardware?: any;
  // eslint-disable-next-line @typescript-eslint/ban-types
  lighting?: LightingPayload;
}

// Cabinet topology describes the display arrangement:
//  - 'multi_tablet': the cabinet is driven by multiple tablets
//  - 'single_tablet_external': a single tablet paired with an external screen
export type CabinetTopology = 'multi_tablet' | 'single_tablet_external';

// screen_role is derived from is_primary: primary (courier/pickup) -> 'pickup',
// non-primary (crew) -> 'management'.
export type ScreenRole = 'pickup' | 'management';

// Per-device roster entry sent on create for multi-tablet cabinets (wire names
// are snake_case). Exactly one entry must have is_master: true.
export type CabinetDevicePayload = {
  serial: string,
  is_master: boolean,
  is_primary: boolean,
  screen_role: ScreenRole,
};

export type CabinetPayload = {
  id?: string,
  store_id: string,
  description: string,
  enable_order_sms?: boolean,
  enable_order_print?: boolean,
  properties: ICabinetProperties,
  business_name?: string,
  business_phone?: string,
  business_address?: string,
  cabinet_location?: string,
  topology?: CabinetTopology,
  devices?: CabinetDevicePayload[],
  pucs: Array<PucPayload>,
  sms_countries?: string[]
};

export type Tag = string;
export type Tags = Array<Tag>;

export interface ICabinet extends IBase {
  id: string
  store_id: string;
  description: string;
  enable_order_sms?: boolean;
  enable_order_print?: boolean;
  pucs: Puc[];
  lockers: Locker[];
  organization?: Organization;
  etag?: string;
  organization_order_sources?: { [order_source: string]: unknown };
  locale: string;
  store_properties?: Record<string, unknown>;
  properties: ICabinetProperties;
  business_name?: string;
  business_phone?: string;
  business_address?: string;
  cabinet_location?: string;
  topology?: CabinetTopology;
  sms_countries?: string[];
  devices?: CabinetConfigDevice[];
}

export class Cabinet implements ICabinet {
  public id: string;
  public store_id: string;
  public description: string;
  public enable_order_sms?: boolean;
  public enable_order_print?: boolean;
  public pucs: Puc[];
  public lockers: Locker[];
  public organization?: Organization;
  public etag?: string;
  public organization_order_sources?: { [order_source: string]: unknown };
  public locale: string;
  public store_properties?: Record<string, unknown>;
  public properties: ICabinetProperties;
  public business_name?: string;
  public business_phone?: string;
  public business_address?: string;
  public cabinet_location?: string;
  public topology?: CabinetTopology;
  public sms_countries?: string[];

  public devices?: CabinetConfigDevice[];

  public tags: Tags = [];
  public isAddon = false;
  public isTemplate = false;

  public constructor(args: ICabinet) {
    this.id = args.id;
    this.store_id = args.store_id;
    this.description = args.description;
    this.enable_order_sms = args.enable_order_sms;
    this.enable_order_print = args.enable_order_print;
    this.properties = args.properties;
    this.business_name = args.business_name;
    this.business_phone = args.business_phone;
    this.business_address = args.business_address;
    this.cabinet_location = args.cabinet_location;
    this.topology = args.topology;
    this.pucs = args.pucs;
    this.sms_countries = args.sms_countries;
    this.lockers = args.lockers;
    this.organization = args.organization;
    this.etag = args.etag;
    this.organization_order_sources = args.organization_order_sources;
    this.locale = args.locale;
    this.store_properties = args.store_properties;

    if (args.devices && args.devices?.length) {
      this.devices = args.devices;
    }
  }

  public hasAddonTemplate() {
    const templateTag = this.tags.find(x => x === 'puc_tool_template_addon');
    return templateTag !== null && templateTag !== undefined;
  }
  
  public getTabletSerial(): string {
    if (this.devices) {
      const droidDevice = this.devices.find((x) => x.device_platform === 'Android');
      if (droidDevice) {
        return droidDevice.device_serial_number;
      }
    }

    return '<no device serial>';
  }
  static create(args: ICabinet): Cabinet {
    return new this(args);
  }
  
  static generateNewCabinet(id: string, storeId: string): Cabinet {
    const newPuc = Puc.generateNewPuc(54, true);
    const newCabinet: ICabinet = {
      id: id,
      store_id: storeId,
      description: 'CHANGE ME',
      enable_order_sms: false,
      enable_order_print: false,
      topology: 'single_tablet_external',
      pucs: [newPuc],
      sms_countries: ['US'],
      lockers: newPuc.boards[0].lockers,
      organization_order_sources: {},
      locale: 'en',
      store_properties: {enable_order_creation_from_ui_app: true},
      properties: {
        app: {
          brand: 'carterhoffmann',
          order: {
            code_end: 999,
            code_start: 100,
            timeout_queue: 1440,
            timeout_cabinet: 1200
          },
          passwords: {
            codes: {
              '008': 'display_order_manager'
            },
            barcodes: {
              '635637648575': 'display_order_manager'
            }
          },
          locale: 'en',
          store_properties: {
            enable_order_creation_from_ui_app: false
          },
          crew_home: 'manage',
          expire_pickups: false,
          enable_transfer_to_counter: false,
          always_transfer_to_counter: false
        },
        modbus_connection: {
          retries: 5,
          timeout_ms: 250
        },
        serial_connection: {
          parity: 'even',
          baud_rate: 115200,
          data_bits: 8,
          stop_bits: 1
        }
      }
    };
    return new Cabinet(newCabinet);
  }
  
  static payload(args: Cabinet, isNew = false): CabinetPayload {
    const payload: CabinetPayload = {
      id: args.id,
      store_id: args.store_id,
      description: args.description,
      enable_order_sms: args.enable_order_sms,
      enable_order_print: args.enable_order_print,
      properties: args.properties,
      business_name: args.business_name,
      business_phone: args.business_phone,
      business_address: args.business_address,
      cabinet_location: args.cabinet_location,
      topology: args.topology,
      pucs: Puc.pucsPayload(args.pucs, isNew),
      sms_countries: args.sms_countries
    };
    
    if (isNew) {
      const {id: _, ...sanitized} = payload;
      return sanitized;
    }

    return payload;
  }
}
